// eslint-disable-next-line no-restricted-imports
import { ClientFragment } from 'api/generated/graphql';
import { parseRepresentativeClientInfoEntity } from 'api/helpers/client/parseRepresentativeClientInfoEntity';
import {
  ClientClassificationType,
  ClientEntity,
  ClientTypeClient,
  TariffType,
} from 'api/types/entity';
import { RiskProfileType } from 'api/types/entity/client/riskProfileType';
import { lodash } from 'helpers';

import { parseCountryEntity } from '../parseCountryEntity';
import { parseDebtsEntity } from '../parseEntity/parseDebtsEntity';
import { parseIdentityDocumentWithFilesEntity } from '../parseEntity/parseIdentityDocumentEntity';
import { parseKycEntity } from '../parseEntity/parseKycEntity';
import { parseManagementType } from '../parseEntity/parseManagementType';
import { parsePepConnectionEntity } from '../parseEntity/parsePepConnectionEntity';
import { parsePersonalAccountEntity } from '../parseEntity/parsePersonalAccountEntity';
import { parseResidentialAddressEntity } from '../parseEntity/parseResidentialAddressEntity';
import { parseServiceContractEntity } from '../parseEntity/parseServiceContractEntity';
import { parseW8BENFormEntity } from '../parseEntity/parseW8BENFormEntity';
import { parseInvoiceEntity } from '../parseInvoiceEntity';
import { parseManagerData } from '../parseManagerData';
import { parsePlaceOfBirth } from '../parsePlaceOfBirth';

import { parseClientOnboardingData } from './parseClientOnboardingData';
import { parseBankAccounts } from './parseEntity/parseBankAccounts';
import { parseBiographyEntity } from './parseEntity/parseBiographyEntity';
import { parseBusinessRelationshipEntity } from './parseEntity/parseBusinessRelationshipEntity';
import { parseCategoryType } from './parseEntity/parseCategoryType';
import { parseClientAccountStatus } from './parseEntity/parseClientAccountStatus';
import { parseClientGenderType } from './parseEntity/parseClientGenderType';
import { parseClientOnboardingStatus } from './parseEntity/parseClientOnboardingStatus';
import { parseClientOpenAccountOrderEntity } from './parseEntity/parseClientOpenAccountOrderEntity';
import { parseClientUserEntity } from './parseEntity/parseClientUser';
import { parseEconomicSectorType } from './parseEntity/parseEconomicSectorType';
import { parseFileDocumentEntity } from './parseEntity/parseFileEntity';
import { parseFinancialPositionEntity } from './parseEntity/parseFinancialPositionEntity';
import { parseFinancingSourcesEntity } from './parseEntity/parseFinancingSourcesEntity';
import { parseInformationUpdateFrequency } from './parseEntity/parseInformationUpdateFrequency';
import { parseKnowledgeAndExperienceEntity } from './parseEntity/parseKnowledgeAndExperienceEntity';
import { parseOrderSubmissionContactsEntity } from './parseEntity/parseOrderSubmissionContactsEntity';
import { parseRiskLevel } from './parseEntity/parseRiskLevel';
import { parseSourcesOfIncomeEntity } from './parseEntity/parseSourcesOfIncomeEntity';
import { parseTaxResidenceEntity } from './parseEntity/parseTaxResidenceEntity';
import { parseWithdrawalAccountsEntity } from './parseEntity/parseWithdrawalAccountsEntity';

export const parseClientEntity = (client: ClientFragment): ClientEntity => ({
  id: client.id,
  brightCode: client.brightCode ?? undefined,
  versionNumber: client.version?.number,
  versionId: client.version?.id,
  onboardingStatus: parseClientOnboardingStatus(client.onboardingStatus),
  accountStatus: parseClientAccountStatus(client.accountStatus),

  firstName: client.firstName,
  lastName: client.lastName,
  middleName: client.middleName ?? undefined,
  email: client.email,
  phone: client.phoneNumber ?? undefined,
  dateOfBirth: new Date(client.dateOfBirth),
  gender: client.gender ? parseClientGenderType(client.gender) : undefined,

  representativeId: client.representativeId ?? undefined,
  representative: client.representative
    ? parseRepresentativeClientInfoEntity(client.representative)
    : undefined,
  citizenships: client.citizenships
    ? lodash.compact(client.citizenships).map(parseCountryEntity)
    : undefined,
  taxResidences: client.taxResidences
    ? lodash.compact(client.taxResidences).map(parseTaxResidenceEntity)
    : undefined,

  placeOfBirth: client.placeOfBirth
    ? parsePlaceOfBirth(client.placeOfBirth)
    : undefined,
  registrationAddress: client.registrationAddress ?? undefined,
  residentialAddress: parseResidentialAddressEntity(client.residentialAddress),

  biography: client.biography
    ? parseBiographyEntity(client.biography)
    : undefined,

  bankAccounts: parseBankAccounts(client.bankAccounts),
  tariff: client.tariff ? TariffType[client.tariff] : undefined,
  financingSources: client.financingSources
    ? parseFinancingSourcesEntity(client.financingSources)
    : undefined,

  sourcesOfIncome: client.sourcesOfIncome
    ? parseSourcesOfIncomeEntity(client.sourcesOfIncome)
    : undefined,

  pepConnection: parsePepConnectionEntity(client),

  debts: parseDebtsEntity(client.debts ?? undefined),

  withdrawalAccounts: parseWithdrawalAccountsEntity(client.withdrawalAccounts),

  orderSubmissionContacts: parseOrderSubmissionContactsEntity(
    client.orderSubmissionContacts
  ),

  code: client.code ?? undefined,
  centralDepositoryAccountNumber:
    client.centralDepositoryAccountNumber ?? undefined,

  centralDepositoryAccountNumberOpeningDate:
    client.centralDepositoryAccountNumberOpeningDate
      ? new Date(client.centralDepositoryAccountNumberOpeningDate)
      : undefined,

  personalAccount: parsePersonalAccountEntity(client.personalAccount),

  manager: client.manager ? parseManagerData(client.manager) : undefined,

  riskLevel: client.riskLevel ? parseRiskLevel(client.riskLevel) : undefined,
  accreditedInvestorStatus: client.accreditedInvestorStatus ?? undefined,
  informationUpdateFrequency: client.informationUpdateFrequency
    ? parseInformationUpdateFrequency(client.informationUpdateFrequency)
    : undefined,

  user: parseClientUserEntity(client.user),

  additionalDocumentFiles: lodash
    .compact(client.additionalDocuments)
    .map(parseFileDocumentEntity),

  additionalInformationFiles: lodash
    .compact(client.additionalInformationDocuments)
    .map(parseFileDocumentEntity),

  signaturesCardFiles: lodash
    .compact(client.signaturesCardDocuments)
    .map(parseFileDocumentEntity),

  openAccountOrder: client.openAccountOrder
    ? parseClientOpenAccountOrderEntity(client.openAccountOrder)
    : undefined,

  documentGroups: client.documentGroups
    ? lodash.compact(client.documentGroups).map(({ documents }) => ({
        documents: lodash.compact(documents).map(parseFileDocumentEntity),
      }))
    : undefined,

  kyc: parseKycEntity(client.kyc),

  category: parseCategoryType(client.category),
  economicSector: parseEconomicSectorType(client.economicSector),
  managementType: parseManagementType(client.managementType),

  clientType: client.clientType
    ? ClientTypeClient[client.clientType]
    : undefined,
  classification: client.classification
    ? ClientClassificationType[client.classification]
    : undefined,
  isInsider: client.isInsider ?? undefined,

  isFinancialMonitoringSubject:
    client.isFinancialMonitoringSubject ?? undefined,
  isKazakhstanResident: client.isKazakhstanResident ?? undefined,
  taxBenefits: client.taxBenefits ?? undefined,

  serviceContract: parseServiceContractEntity(client.serviceContract),

  riskLevelReviewDate: client.riskLevelReviewDate
    ? new Date(client.riskLevelReviewDate)
    : undefined,
  lastInformationUpdateDate: client.lastInformationUpdateDate
    ? new Date(client.lastInformationUpdateDate)
    : undefined,
  lastOperationMonitoringResults:
    client.lastOperationMonitoringResults ?? undefined,
  usedServices: client.usedServices ?? undefined,

  selfieFiles: client.selfieDocuments
    ? lodash.compact(client.selfieDocuments).map(parseFileDocumentEntity)
    : undefined,

  identityDocument: client.identityDocument
    ? parseIdentityDocumentWithFilesEntity(client.identityDocument)
    : undefined,

  onboarding: parseClientOnboardingData(client),

  businessRelationship: client.businessRelationship
    ? parseBusinessRelationshipEntity(client.businessRelationship)
    : undefined,
  riskProfile: client.riskProfile
    ? RiskProfileType[client.riskProfile]
    : undefined,

  financialPosition: client.financialPosition
    ? parseFinancialPositionEntity(client.financialPosition)
    : undefined,

  W8BENForm: client.W8BENForm
    ? parseW8BENFormEntity(client.W8BENForm)
    : undefined,

  knowledgeAndExperience: client.knowledgeAndExperience
    ? parseKnowledgeAndExperienceEntity(client.knowledgeAndExperience)
    : undefined,

  invoice: parseInvoiceEntity(client.invoice),

  companySignedNoticeOfInvestmentAccountOpeningDocument:
    client.companySignedNoticeOfInvestmentAccountOpeningDocument
      ? parseFileDocumentEntity(
          client.companySignedNoticeOfInvestmentAccountOpeningDocument
        )
      : undefined,

  companySignedAgreementDocument_Finance:
    client.companySignedAgreementDocument_Finance
      ? parseFileDocumentEntity(client.companySignedAgreementDocument_Finance)
      : undefined,

  signedApplicationStandardAgreementDocument_Finance:
    client.signedApplicationStandardAgreementDocument_Finance
      ? parseFileDocumentEntity(
          client.signedApplicationStandardAgreementDocument_Finance
        )
      : undefined,

  signedOpenSubAccountOrderDocument_Finance:
    client.signedOpenSubAccountOrderDocument_Finance
      ? parseFileDocumentEntity(
          client.signedOpenSubAccountOrderDocument_Finance
        )
      : undefined,

  isBrightSyncEnabled: client.isBrightSyncEnabled,
});
